import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import { type ICreatePremio, type IPremio } from '@/api/campaigns/premio'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const premioSchema = z.object({
  descricao: z.string().min(1, 'Descrição é obrigatória'),
  posicao: z.preprocess(
    (val) => Number(val),
    z
      .number({ required_error: 'Posição é obrigatória' })
      .min(1, 'A posição deve ser maior que 0'),
  ),
  campanha_Id: z.number(),
})

type PremioSchema = z.infer<typeof premioSchema>

interface PremioModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: ICreatePremio) => void
  premio?: IPremio | null
}

export function PremioModal({
  isOpen,
  onOpenChange,
  onSubmit,
  premio,
}: PremioModalProps) {
  const { rifaId } = useParams()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PremioSchema>({
    resolver: zodResolver(premioSchema),
    defaultValues: {
      descricao: '',
      posicao: 1,
      campanha_Id: Number(rifaId),
    },
  })

  useEffect(() => {
    reset({
      descricao: premio?.descricao ?? '',
      posicao: premio?.posicao ?? 1,
      campanha_Id: premio?.campanha_Id ?? Number(rifaId),
    })
  }, [premio, reset, rifaId])

  const handleFormSubmit = (data: PremioSchema) => {
    onSubmit(data)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {premio ? 'Editar Prêmio' : 'Adicionar Prêmio'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="descricao">Descrição</Label>
            <Input id="descricao" {...register('descricao')} />
            {errors.descricao && (
              <p className="text-sm text-red-500">{errors.descricao.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="posicao">Posição Prêmio</Label>
            <Input
              id="posicao"
              type="number"
              min={1}
              {...register('posicao')}
            />
            {errors.posicao && (
              <p className="text-sm text-red-500">{errors.posicao.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="submit">Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
